/**
 * BulkSchedulerDialog — schedule N pieces of content across multiple platforms.
 *
 * UX flow:
 *  1. User picks platforms (each with its own post type)
 *  2. User picks an FB Page / IG account / LinkedIn org if applicable
 *  3. User uploads N media items, can reorder + add per-item caption override
 *  4. User picks a cadence: interval | daily slots | manual datetimes
 *  5. UI computes scheduledAt[i] from the rule and submits to /api/scheduler/bulk
 *
 * Backend stays cadence-agnostic — we ship explicit timestamps per item.
 */

import { useMemo, useState, useEffect } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import {
  Plus,
  Trash2,
  ArrowUp,
  ArrowDown,
  Upload,
  Loader2,
  Sparkles,
  AlertCircle,
} from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { FriendlyErrorMapper } from "@/lib/errors/FriendlyErrorMapper";
import { apiRequest, uploadSchedulerMedia } from "@/lib/queryClient";
import { PresetsPanel } from "@/components/scheduler/PresetsPanel";
import { getTikTokValidity } from "@/components/scheduler/presets/tiktok-compliance";
import { DEFAULT_SCHEDULER_PRESETS } from "@shared/schema";
import type {
  ScheduledPostPlatform,
  ScheduledMetaPostType,
  ScheduledPostMediaType,
  SchedulerPresets,
} from "@shared/schema";

interface MetaPage {
  id: string;
  name: string;
  instagramBusinessAccount?: { id: string; name?: string; username?: string };
}

interface LinkedInOrg {
  id: string;
  name: string;
}

// YouTube + LinkedIn widoczne tylko dla super_admin (gating w komponencie)
const ALL_PLATFORMS: ScheduledPostPlatform[] = ["facebook", "instagram", "tiktok"];
const ALL_PLATFORMS_SUPER_ADMIN: ScheduledPostPlatform[] = [
  "facebook",
  "instagram",
  "linkedin",
  "youtube",
  "tiktok",
];

const POST_TYPE_BY_PLATFORM: Record<ScheduledPostPlatform, ScheduledMetaPostType[]> = {
  facebook: ["post", "reel"],
  instagram: ["post", "reel", "trial_reel", "story"],
  linkedin: ["post"],
  youtube: ["post", "reel"],
  tiktok: ["post", "reel"],
};

const POST_TYPE_LABEL: Record<ScheduledMetaPostType, string> = {
  post: "Post",
  reel: "Reel",
  trial_reel: "Trial Reel",
  story: "Story",
};

const PLATFORM_LABEL: Record<ScheduledPostPlatform, string> = {
  facebook: "Facebook",
  instagram: "Instagram",
  linkedin: "LinkedIn",
  youtube: "YouTube",
  tiktok: "TikTok",
};

const MAX_ITEMS = 100;

type CadenceMode = "interval" | "daily_slots" | "manual";

interface BulkItem {
  id: string;
  mediaUrl: string;
  mediaType: ScheduledPostMediaType;
  fileName: string;
  caption: string;
  uploading: boolean;
  uploadProgress: number;
  uploadError?: string;
  manualScheduledAt?: string;
  aiGenerating?: boolean;
}

interface BulkSubmitItemPayload {
  scheduledAt: string;
  mediaType: ScheduledPostMediaType;
  mediaUrls: string[];
  caption?: string;
  title?: string;
}

interface BulkSubmitPayload {
  platforms: Partial<Record<ScheduledPostPlatform, ScheduledMetaPostType>>;
  items: BulkSubmitItemPayload[];
  sharedCaption?: string;
  pageId?: string;
  pageName?: string;
  igAccountId?: string;
  igAccountName?: string;
  forUserId?: string;
  presets?: SchedulerPresets;
}

/**
 * Minimal subset of ScheduledPost the dialog needs to hydrate edit state.
 * We accept this as a structural type so callers don't have to import the full
 * server schema type — anything with these fields works.
 */
export interface BulkEditPost {
  id: string;
  platform: ScheduledPostPlatform;
  postType: ScheduledMetaPostType;
  status: string;
  caption: string | null;
  title: string | null;
  mediaType: ScheduledPostMediaType;
  mediaUrls: string[] | null;
  scheduledAt: Date | string | null;
  pageId: string | null;
  pageName: string | null;
  igAccountId: string | null;
  igAccountName: string | null;
  batchId: string | null;
  presets?: SchedulerPresets | null;
}

interface BulkSchedulerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  pages: MetaPage[];
  linkedInOrganizations: LinkedInOrg[];
  connectedPlatforms: ScheduledPostPlatform[];
  forUserId?: string;
  /** When set, dialog opens in edit mode and hydrates from these posts (all rows of one batch). */
  editBatch?: { batchId: string; posts: BulkEditPost[] };
}

function generateId(): string {
  return `item-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function inferMediaType(mimeType: string): ScheduledPostMediaType {
  if (mimeType.startsWith("video/")) return "video";
  if (mimeType.startsWith("image/")) return "image";
  return "image";
}

function toLocalDatetimeValue(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** Build an array of scheduledAt timestamps from the selected cadence rule. */
function computeSchedule(opts: {
  mode: CadenceMode;
  itemCount: number;
  intervalStart: string;
  intervalHours: number;
  dailyStartDate: string;
  dailyTimes: string[];
  dailyDays: number;
  manualValues: (string | undefined)[];
}): (Date | null)[] {
  const { mode, itemCount } = opts;
  if (itemCount === 0) return [];

  if (mode === "interval") {
    const start = opts.intervalStart ? new Date(opts.intervalStart) : null;
    if (!start || isNaN(start.getTime())) return Array(itemCount).fill(null);
    const stepMs = Math.max(0.5, opts.intervalHours) * 3600 * 1000;
    return Array.from({ length: itemCount }, (_, i) => new Date(start.getTime() + i * stepMs));
  }

  if (mode === "daily_slots") {
    if (!opts.dailyStartDate || opts.dailyTimes.length === 0) return Array(itemCount).fill(null);
    const startDay = new Date(opts.dailyStartDate + "T00:00:00");
    if (isNaN(startDay.getTime())) return Array(itemCount).fill(null);

    const slots: Date[] = [];
    for (let dayOffset = 0; dayOffset < opts.dailyDays && slots.length < itemCount; dayOffset++) {
      for (const time of opts.dailyTimes) {
        if (slots.length >= itemCount) break;
        const [h, m] = time.split(":").map(Number);
        const slot = new Date(startDay);
        slot.setDate(slot.getDate() + dayOffset);
        slot.setHours(h ?? 0, m ?? 0, 0, 0);
        slots.push(slot);
      }
    }
    while (slots.length < itemCount) slots.push(null as unknown as Date);
    return slots;
  }

  // manual
  return opts.manualValues.map((v) => (v ? new Date(v) : null));
}

export default function BulkSchedulerDialog({
  open,
  onOpenChange,
  pages,
  linkedInOrganizations,
  connectedPlatforms,
  forUserId,
  editBatch,
}: BulkSchedulerDialogProps) {
  const isEditMode = Boolean(editBatch);
  const { t } = useTranslation();
  const { toast } = useToast();
  const { user } = useAuth();
  const isSuperAdmin = (user as { role?: string } | undefined)?.role === "super_admin";
  const queryClient = useQueryClient();

  const [platformPostTypes, setPlatformPostTypes] = useState<
    Partial<Record<ScheduledPostPlatform, ScheduledMetaPostType>>
  >({});
  const [pageId, setPageId] = useState<string>("");
  const [igAccountId, setIgAccountId] = useState<string>("");
  const [linkedInOrgId, setLinkedInOrgId] = useState<string>("");
  const [items, setItems] = useState<BulkItem[]>([]);
  const [sharedCaption, setSharedCaption] = useState<string>("");
  const [presets, setPresets] = useState<SchedulerPresets>(DEFAULT_SCHEDULER_PRESETS);
  const [tiktokBlocked, setTiktokBlocked] = useState(false);

  const [cadenceMode, setCadenceMode] = useState<CadenceMode>("interval");
  const [intervalStart, setIntervalStart] = useState<string>(() => {
    const d = new Date();
    d.setMinutes(d.getMinutes() + 30, 0, 0);
    return toLocalDatetimeValue(d);
  });
  const [intervalHours, setIntervalHours] = useState<number>(24);
  const [dailyStartDate, setDailyStartDate] = useState<string>(() => {
    const d = new Date();
    return d.toISOString().slice(0, 10);
  });
  const [dailyDays, setDailyDays] = useState<number>(7);
  const [dailyTimesText, setDailyTimesText] = useState<string>("18:00");

  const dailyTimes = useMemo(
    () =>
      dailyTimesText
        .split(",")
        .map((s) => s.trim())
        .filter((s) => /^\d{1,2}:\d{2}$/.test(s)),
    [dailyTimesText]
  );

  const computedTimestamps = useMemo(
    () =>
      computeSchedule({
        mode: cadenceMode,
        itemCount: items.length,
        intervalStart,
        intervalHours,
        dailyStartDate,
        dailyTimes,
        dailyDays,
        manualValues: items.map((it) => it.manualScheduledAt),
      }),
    [cadenceMode, items, intervalStart, intervalHours, dailyStartDate, dailyTimes, dailyDays]
  );

  // Reset / hydrate form whenever dialog opens
  useEffect(() => {
    if (!open) return;

    if (!editBatch) {
      setPlatformPostTypes({});
      setPageId("");
      setIgAccountId("");
      setLinkedInOrgId("");
      setItems([]);
      setSharedCaption("");
      setPresets(DEFAULT_SCHEDULER_PRESETS);
      setTiktokBlocked(false);
      return;
    }

    // Edit mode: rebuild form from existing batch rows. Only consider draft/scheduled
    // (published rows are immutable — the dialog can't reschedule them).
    const editable = editBatch.posts.filter(
      (p) => p.status === "draft" || p.status === "scheduled"
    );

    const platforms: Partial<Record<ScheduledPostPlatform, ScheduledMetaPostType>> = {};
    for (const p of editable) {
      platforms[p.platform] = p.postType;
    }
    setPlatformPostTypes(platforms);

    // Page / IG / LinkedIn — assume uniform across batch (UI doesn't support per-platform pages)
    const firstFb = editable.find((p) => p.platform === "facebook");
    const firstIg = editable.find((p) => p.platform === "instagram");
    const firstLi = editable.find((p) => p.platform === "linkedin");
    setPageId(firstFb?.pageId ?? "");
    setIgAccountId(firstIg?.igAccountId ?? "");
    setLinkedInOrgId(firstLi?.pageId ?? "");

    // Presets are uniform across the batch (BulkScheduleService writes one blob per row).
    setPresets(editable.find((p) => p.presets)?.presets ?? DEFAULT_SCHEDULER_PRESETS);
    setTiktokBlocked(false);

    // Group editable rows into items by scheduledAt (rows for the same item across platforms
    // share the same scheduledAt + media; per-platform variants are rebuilt via platformPostTypes).
    const itemMap = new Map<string, BulkItem>();
    for (const p of editable) {
      if (!p.scheduledAt) continue;
      const ts = typeof p.scheduledAt === "string" ? p.scheduledAt : p.scheduledAt.toISOString();
      if (itemMap.has(ts)) continue;
      const urls = p.mediaUrls ?? [];
      itemMap.set(ts, {
        id: generateId(),
        mediaUrl: urls[0] ?? "",
        mediaType: p.mediaType,
        fileName: urls[0]?.split("/").pop() ?? "(istniejący plik)",
        caption: p.caption ?? "",
        uploading: false,
        uploadProgress: 100,
        manualScheduledAt: toLocalDatetimeValue(new Date(ts)),
      });
    }
    const sortedItems = Array.from(itemMap.values()).sort((a, b) => {
      const ta = new Date(a.manualScheduledAt ?? 0).getTime();
      const tb = new Date(b.manualScheduledAt ?? 0).getTime();
      return ta - tb;
    });
    setItems(sortedItems);

    // Default to manual mode in edit so the user can see and tweak each scheduledAt.
    setCadenceMode("manual");

    // sharedCaption: pick the most-frequent caption — likely the one used as the original shared.
    const counts = new Map<string, number>();
    for (const p of editable) {
      const c = p.caption?.trim() ?? "";
      if (!c) continue;
      counts.set(c, (counts.get(c) ?? 0) + 1);
    }
    let bestCaption = "";
    let bestCount = 0;
    for (const [c, n] of counts)
      if (n > bestCount) {
        bestCaption = c;
        bestCount = n;
      }
    setSharedCaption(bestCaption);
  }, [open, editBatch]);

  const selectedPlatforms = Object.keys(platformPostTypes) as ScheduledPostPlatform[];
  const needsFbPage = selectedPlatforms.includes("facebook");
  const needsIgAccount = selectedPlatforms.includes("instagram");
  const needsLinkedInOrg = selectedPlatforms.includes("linkedin");
  const hasTrialReel = Object.values(platformPostTypes).includes("trial_reel");

  // TikTok Content Sharing Guidelines: privacy must be explicitly chosen (no default),
  // otherwise the publisher falls back to SELF_ONLY — the batch must not be submittable.
  const tiktokSelected = selectedPlatforms.includes("tiktok");
  const tiktokValidity = tiktokSelected ? getTikTokValidity(presets.tiktok) : null;
  const tiktokInvalid = tiktokValidity !== null || (tiktokSelected && tiktokBlocked);
  const tiktokAllPhotos = items.length > 0 && items.every((it) => it.mediaType === "image");

  function togglePlatform(platform: ScheduledPostPlatform, checked: boolean) {
    setPlatformPostTypes((prev) => {
      const next = { ...prev };
      if (checked) {
        next[platform] = POST_TYPE_BY_PLATFORM[platform][0];
      } else {
        delete next[platform];
      }
      return next;
    });
  }

  function changePostType(platform: ScheduledPostPlatform, postType: ScheduledMetaPostType) {
    setPlatformPostTypes((prev) => ({ ...prev, [platform]: postType }));
  }

  async function handleFiles(files: FileList | null) {
    if (!files) return;
    if (items.length + files.length > MAX_ITEMS) {
      toast({
        title: "Limit przekroczony",
        description: `Maksymalnie ${MAX_ITEMS} elementów w batchu.`,
        variant: "destructive",
      });
      return;
    }

    for (const file of Array.from(files)) {
      const id = generateId();
      const mediaType = inferMediaType(file.type);
      setItems((prev) => [
        ...prev,
        {
          id,
          mediaUrl: "",
          mediaType,
          fileName: file.name,
          caption: "",
          uploading: true,
          uploadProgress: 0,
        },
      ]);

      try {
        const data = await uploadSchedulerMedia({
          file,
          forUserId,
          onProgress: (percent) => {
            setItems((prev) =>
              prev.map((it) => (it.id === id ? { ...it, uploadProgress: percent } : it))
            );
          },
        });

        setItems((prev) =>
          prev.map((it) =>
            it.id === id
              ? {
                  ...it,
                  uploading: false,
                  uploadProgress: 100,
                  mediaUrl: data.url,
                  mediaType: data.mediaType,
                }
              : it
          )
        );
      } catch (err) {
        const friendly = FriendlyErrorMapper.map(err);
        setItems((prev) =>
          prev.map((it) =>
            it.id === id ? { ...it, uploading: false, uploadError: friendly.description } : it
          )
        );
      }
    }
  }

  function removeItem(id: string) {
    setItems((prev) => prev.filter((it) => it.id !== id));
  }

  function moveItem(id: string, direction: -1 | 1) {
    setItems((prev) => {
      const idx = prev.findIndex((it) => it.id === id);
      if (idx < 0) return prev;
      const target = idx + direction;
      if (target < 0 || target >= prev.length) return prev;
      const next = [...prev];
      [next[idx], next[target]] = [next[target], next[idx]];
      return next;
    });
  }

  function setItemCaption(id: string, caption: string) {
    setItems((prev) => prev.map((it) => (it.id === id ? { ...it, caption } : it)));
  }

  function setItemManualSchedule(id: string, value: string) {
    setItems((prev) => prev.map((it) => (it.id === id ? { ...it, manualScheduledAt: value } : it)));
  }

  async function generateAiCaptionForItem(item: BulkItem): Promise<string | null> {
    if (item.mediaType !== "video" || !item.mediaUrl) {
      toast({
        title: "AI caption",
        description: "Działa tylko dla wgranych wideo.",
        variant: "destructive",
      });
      return null;
    }
    const platform =
      (Object.keys(platformPostTypes)[0] as ScheduledPostPlatform | undefined) ?? "instagram";
    const postType = platformPostTypes[platform] ?? "reel";
    setItems((prev) => prev.map((it) => (it.id === item.id ? { ...it, aiGenerating: true } : it)));
    try {
      const data = (await apiRequest("POST", "/api/scheduler/generate-caption-from-video", {
        platform,
        postType,
        videoUrl: item.mediaUrl,
      })) as { caption: string; transcript: string };
      setItems((prev) =>
        prev.map((it) =>
          it.id === item.id ? { ...it, caption: data.caption, aiGenerating: false } : it
        )
      );
      return data.caption;
    } catch (err) {
      setItems((prev) =>
        prev.map((it) => (it.id === item.id ? { ...it, aiGenerating: false } : it))
      );
      toast({
        title: "AI nie wygenerowało podpisu",
        description: FriendlyErrorMapper.map(err).description,
        variant: "destructive",
      });
      return null;
    }
  }

  async function generateAllAiCaptions() {
    const videoItems = items.filter(
      (it) => it.mediaType === "video" && it.mediaUrl && !it.aiGenerating
    );
    if (videoItems.length === 0) {
      toast({
        title: "AI caption",
        description: "Brak wgranych video do transkrypcji.",
        variant: "destructive",
      });
      return;
    }
    // Sequential to keep Whisper rate limits + cost predictable
    for (const item of videoItems) {
      await generateAiCaptionForItem(item);
    }
  }

  const submitMutation = useMutation({
    mutationFn: (payload: BulkSubmitPayload) =>
      isEditMode && editBatch
        ? apiRequest("PUT", `/api/scheduler/bulk/${editBatch.batchId}`, payload)
        : apiRequest("POST", "/api/scheduler/bulk", payload),
    onSuccess: (data: { posts?: unknown[]; batchId?: string }) => {
      queryClient.invalidateQueries({ queryKey: ["/api/scheduler"] });
      toast({
        title: isEditMode ? "Batch zaktualizowany" : "Batch zaplanowany",
        description: `${isEditMode ? "Zapisano" : "Utworzono"} ${data?.posts?.length ?? 0} postów (batch ${data?.batchId?.slice(0, 8) ?? ""}).`,
      });
      onOpenChange(false);
    },
    onError: (err: unknown) => {
      toast({
        title: isEditMode
          ? "Nie udało się zaktualizować batcha"
          : "Nie udało się zaplanować batcha",
        description: FriendlyErrorMapper.map(err).description,
        variant: "destructive",
      });
    },
  });

  function buildPayload(): BulkSubmitPayload | { error: string } {
    if (selectedPlatforms.length === 0) return { error: "Wybierz przynajmniej jedną platformę." };
    if (items.length === 0) return { error: "Dodaj przynajmniej jeden plik." };
    if (items.some((it) => it.uploading)) return { error: "Poczekaj na zakończenie uploadu." };
    if (items.some((it) => !it.mediaUrl))
      return { error: "Niektóre pliki nie zostały poprawnie wgrane." };

    if (needsFbPage && !pageId) return { error: "Wybierz stronę Facebook." };
    if (needsIgAccount && !igAccountId) return { error: "Wybierz konto Instagram." };
    if (needsLinkedInOrg && !linkedInOrgId) return { error: "Wybierz organizację LinkedIn." };
    if (tiktokValidity) {
      return { error: t(`contentScheduler.presets.tiktok.gate.${tiktokValidity}`) };
    }

    const payloadItems: BulkSubmitItemPayload[] = [];
    for (let i = 0; i < items.length; i++) {
      const it = items[i];
      const ts = computedTimestamps[i];
      if (!ts || isNaN(ts.getTime()))
        return { error: `Element #${i + 1}: brak prawidłowej daty publikacji.` };
      payloadItems.push({
        scheduledAt: ts.toISOString(),
        mediaType: it.mediaType,
        mediaUrls: [it.mediaUrl],
        caption: it.caption.trim() || undefined,
      });
    }

    const selectedPage = pages.find((p) => p.id === pageId);
    const selectedOrg = linkedInOrganizations.find((o) => o.id === linkedInOrgId);

    return {
      platforms: platformPostTypes,
      items: payloadItems,
      sharedCaption: sharedCaption.trim() || undefined,
      pageId: needsFbPage ? pageId : needsLinkedInOrg ? linkedInOrgId : undefined,
      pageName: needsFbPage ? selectedPage?.name : needsLinkedInOrg ? selectedOrg?.name : undefined,
      igAccountId: igAccountId || undefined,
      igAccountName: igAccountId ? selectedPage?.instagramBusinessAccount?.username : undefined,
      forUserId,
      presets,
    };
  }

  function handleSubmit() {
    const result = buildPayload();
    if ("error" in result) {
      toast({ title: "Walidacja", description: result.error, variant: "destructive" });
      return;
    }
    submitMutation.mutate(result);
  }

  const isSubmitting = submitMutation.isPending;
  const fallbackPlatforms = isSuperAdmin ? ALL_PLATFORMS_SUPER_ADMIN : ALL_PLATFORMS;
  const platformOptions = connectedPlatforms.length > 0 ? connectedPlatforms : fallbackPlatforms;
  const igAccounts = pages.filter((p) => p.instagramBusinessAccount);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[900px] w-[95vw] h-[85vh] max-h-[800px] p-0 bg-[#1a1a1a] border-white/[0.08] overflow-hidden flex flex-col">
        <DialogHeader className="px-6 py-4 border-b border-white/[0.08]">
          <DialogTitle className="text-white text-lg font-semibold">
            {isEditMode ? "Edytuj batch" : "Plan zbiorczy (bulk schedule)"}
          </DialogTitle>
          <p className="text-white/50 text-sm">
            {isEditMode
              ? "Zmieniasz draft/scheduled posty z tego batcha. Już opublikowane zostają nietknięte."
              : "Zaplanuj wiele postów / rolek próbnych jednym zamachem."}
          </p>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">
          {/* Platform picker */}
          <section>
            <Label className="text-white/80 text-sm font-medium mb-2 block">
              Platformy & typ postu
            </Label>
            <div className="space-y-2">
              {platformOptions.map((platform) => {
                const checked = selectedPlatforms.includes(platform);
                const postType = platformPostTypes[platform];
                return (
                  <div
                    key={platform}
                    className="flex items-center gap-3 px-3 py-2 rounded-lg bg-white/[0.03] border border-white/[0.06]"
                  >
                    <Checkbox
                      checked={checked}
                      onCheckedChange={(v) => togglePlatform(platform, v === true)}
                      id={`platform-${platform}`}
                    />
                    <Label
                      htmlFor={`platform-${platform}`}
                      className="text-white text-sm flex-1 cursor-pointer"
                    >
                      {PLATFORM_LABEL[platform]}
                    </Label>
                    {checked && (
                      <Select
                        value={postType}
                        onValueChange={(v) => changePostType(platform, v as ScheduledMetaPostType)}
                      >
                        <SelectTrigger className="w-[140px] h-8 bg-black/40 border-white/[0.08] text-white text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {POST_TYPE_BY_PLATFORM[platform].map((t) => (
                            <SelectItem key={t} value={t}>
                              {POST_TYPE_LABEL[t]}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  </div>
                );
              })}
            </div>
          </section>

          {/* Trial Reel requirements notice */}
          {hasTrialReel && (
            <section className="rounded-lg bg-amber-500/10 border border-amber-500/30 p-3">
              <p className="text-amber-200 text-sm font-medium mb-1">
                ⚠️ Wymogi Trial Reels (Meta API)
              </p>
              <ul className="text-amber-100/85 text-xs space-y-0.5 list-disc list-inside">
                <li>
                  Konto IG = <strong>Business</strong> lub <strong>Creator</strong> (nie Personal)
                </li>
                <li>
                  <strong>Min. 1000 obserwujących</strong> na koncie IG
                </li>
                <li>
                  <strong>Admin role</strong> na powiązanej FB Page (Meta Business Manager → Page
                  Access)
                </li>
                <li>
                  App Review zatwierdzony dla <code>instagram_content_publish</code>
                </li>
              </ul>
              <p className="text-amber-100/60 text-[11px] mt-1.5">
                Pełny troubleshooting:{" "}
                <a
                  href="/docs/scheduler-publish-troubleshooting.md"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline hover:text-amber-100"
                >
                  scheduler-publish-troubleshooting.md
                </a>
              </p>
            </section>
          )}

          {/* Account picker */}
          {(needsFbPage || needsIgAccount || needsLinkedInOrg) && (
            <section className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {needsFbPage && (
                <div>
                  <Label className="text-white/80 text-sm mb-1 block">Strona Facebook</Label>
                  <Select value={pageId} onValueChange={setPageId}>
                    <SelectTrigger className="bg-black/40 border-white/[0.08] text-white">
                      <SelectValue placeholder="Wybierz stronę" />
                    </SelectTrigger>
                    <SelectContent>
                      {pages.map((p) => (
                        <SelectItem key={p.id} value={p.id}>
                          {p.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              {needsIgAccount && (
                <div>
                  <Label className="text-white/80 text-sm mb-1 block">Konto Instagram</Label>
                  <Select value={igAccountId} onValueChange={setIgAccountId}>
                    <SelectTrigger className="bg-black/40 border-white/[0.08] text-white">
                      <SelectValue placeholder="Wybierz konto IG" />
                    </SelectTrigger>
                    <SelectContent>
                      {igAccounts.map((p) => (
                        <SelectItem
                          key={p.instagramBusinessAccount!.id}
                          value={p.instagramBusinessAccount!.id}
                        >
                          @{p.instagramBusinessAccount!.username ?? p.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              {needsLinkedInOrg && (
                <div>
                  <Label className="text-white/80 text-sm mb-1 block">Organizacja LinkedIn</Label>
                  <Select value={linkedInOrgId} onValueChange={setLinkedInOrgId}>
                    <SelectTrigger className="bg-black/40 border-white/[0.08] text-white">
                      <SelectValue placeholder="Wybierz organizację" />
                    </SelectTrigger>
                    <SelectContent>
                      {linkedInOrganizations.map((o) => (
                        <SelectItem key={o.id} value={o.id}>
                          {o.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </section>
          )}

          {/* Per-platform presets (TikTok privacy/interakcje, IG collaborators, …) */}
          {selectedPlatforms.length > 0 && (
            <section>
              <Label className="text-white/80 text-sm font-medium mb-2 block">
                Ustawienia platform
              </Label>
              <PresetsPanel
                presets={presets}
                selectedPlatforms={selectedPlatforms}
                threadsSelected={false}
                onChange={setPresets}
                tiktokForUserId={forUserId}
                tiktokIsPhoto={tiktokAllPhotos}
                tiktokOnBlockedChange={setTiktokBlocked}
              />
            </section>
          )}

          {/* Cadence picker */}
          <section>
            <Label className="text-white/80 text-sm font-medium mb-2 block">
              Harmonogram publikacji
            </Label>
            <RadioGroup
              value={cadenceMode}
              onValueChange={(v) => setCadenceMode(v as CadenceMode)}
              className="grid grid-cols-3 gap-2 mb-3"
            >
              {[
                { value: "interval", label: "Co X godzin" },
                { value: "daily_slots", label: "Codziennie o…" },
                { value: "manual", label: "Ręcznie" },
              ].map((opt) => (
                <Label
                  key={opt.value}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer text-sm ${cadenceMode === opt.value ? "bg-[#ef3e29]/12 border-[#ef3e29]/40 text-white" : "bg-white/[0.03] border-white/[0.06] text-white/70"}`}
                >
                  <RadioGroupItem value={opt.value} className="text-[#ef3e29]" />
                  {opt.label}
                </Label>
              ))}
            </RadioGroup>

            {cadenceMode === "interval" && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-white/60 text-xs mb-1 block">Pierwszy post</Label>
                  <Input
                    type="datetime-local"
                    value={intervalStart}
                    onChange={(e) => setIntervalStart(e.target.value)}
                    className="bg-black/40 border-white/[0.08] text-white"
                  />
                </div>
                <div>
                  <Label className="text-white/60 text-xs mb-1 block">Odstęp (godziny)</Label>
                  <Input
                    type="number"
                    min="0.5"
                    step="0.5"
                    value={intervalHours}
                    onChange={(e) => setIntervalHours(Number(e.target.value) || 0)}
                    className="bg-black/40 border-white/[0.08] text-white"
                  />
                </div>
              </div>
            )}

            {cadenceMode === "daily_slots" && (
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-white/60 text-xs mb-1 block">Data startu</Label>
                    <Input
                      type="date"
                      value={dailyStartDate}
                      onChange={(e) => setDailyStartDate(e.target.value)}
                      className="bg-black/40 border-white/[0.08] text-white"
                    />
                  </div>
                  <div>
                    <Label className="text-white/60 text-xs mb-1 block">Liczba dni</Label>
                    <Input
                      type="number"
                      min="1"
                      max="60"
                      value={dailyDays}
                      onChange={(e) => setDailyDays(Number(e.target.value) || 1)}
                      className="bg-black/40 border-white/[0.08] text-white"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-white/60 text-xs mb-1 block">
                    Godziny dziennie (przecinek, format HH:MM)
                  </Label>
                  <Input
                    value={dailyTimesText}
                    onChange={(e) => setDailyTimesText(e.target.value)}
                    placeholder="09:00, 18:00"
                    className="bg-black/40 border-white/[0.08] text-white"
                  />
                </div>
              </div>
            )}

            {cadenceMode === "manual" && (
              <p className="text-white/50 text-xs">
                Ustaw datę publikacji indywidualnie przy każdym elemencie poniżej.
              </p>
            )}
          </section>

          {/* Shared caption */}
          <section>
            <Label className="text-white/80 text-sm font-medium mb-1 block">
              Treść wspólna (fallback)
            </Label>
            <Textarea
              value={sharedCaption}
              onChange={(e) => setSharedCaption(e.target.value)}
              placeholder="Treść używana gdy element nie ma własnego captiona…"
              className="bg-black/40 border-white/[0.08] text-white min-h-[60px]"
            />
          </section>

          {/* Items */}
          <section>
            <div className="flex items-center justify-between mb-2">
              <Label className="text-white/80 text-sm font-medium">Pliki ({items.length})</Label>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={generateAllAiCaptions}
                  disabled={items.length === 0 || items.some((it) => it.aiGenerating)}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-purple-500/15 hover:bg-purple-500/25 text-purple-200 text-xs disabled:opacity-30 disabled:cursor-not-allowed border border-purple-500/25"
                  title="Wygeneruj caption z transkrypcji audio dla wszystkich video"
                >
                  <Sparkles size={14} />
                  AI ze wszystkich
                </button>
                <label className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/[0.06] hover:bg-white/[0.1] text-white/80 text-xs cursor-pointer">
                  <Upload size={14} />
                  Dodaj pliki
                  <input
                    type="file"
                    multiple
                    accept="image/*,video/*"
                    className="hidden"
                    onChange={(e) => {
                      handleFiles(e.target.files);
                      e.target.value = "";
                    }}
                  />
                </label>
              </div>
            </div>

            {items.length === 0 ? (
              <p className="text-white/40 text-sm py-6 text-center border border-dashed border-white/10 rounded-lg">
                Dodaj wideo / grafiki, które chcesz zaplanować.
              </p>
            ) : (
              <div className="space-y-2">
                {items.map((it, idx) => {
                  const ts = computedTimestamps[idx];
                  const tsLabel =
                    ts && !isNaN(ts.getTime())
                      ? ts.toLocaleString("pl-PL", { dateStyle: "short", timeStyle: "short" })
                      : "— brak daty —";
                  return (
                    <div
                      key={it.id}
                      className="rounded-lg bg-white/[0.03] border border-white/[0.06] p-3"
                    >
                      <div className="flex items-start gap-3">
                        <div className="text-white/60 text-xs w-6 flex-shrink-0 pt-1">
                          {idx + 1}.
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-white text-sm truncate">{it.fileName}</span>
                            <span className="text-white/40 text-xs flex-shrink-0">
                              [{it.mediaType}]
                            </span>
                            {it.uploading && (
                              <Loader2 size={12} className="animate-spin text-white/60" />
                            )}
                            {it.uploadError && (
                              <span className="text-red-400 text-xs">{it.uploadError}</span>
                            )}
                          </div>
                          <div className="text-[#ef3e29] text-xs mb-2">📅 {tsLabel}</div>
                          {cadenceMode === "manual" && (
                            <Input
                              type="datetime-local"
                              value={it.manualScheduledAt ?? ""}
                              onChange={(e) => setItemManualSchedule(it.id, e.target.value)}
                              className="bg-black/40 border-white/[0.08] text-white text-xs h-8 mb-2"
                            />
                          )}
                          <div className="relative">
                            <Textarea
                              value={it.caption}
                              onChange={(e) => setItemCaption(it.id, e.target.value)}
                              placeholder="Caption dla tego elementu (opcjonalnie, nadpisuje wspólny)…"
                              className="bg-black/40 border-white/[0.08] text-white text-xs min-h-[44px] pr-9"
                            />
                            {it.mediaType === "video" && it.mediaUrl && (
                              <button
                                type="button"
                                onClick={() => generateAiCaptionForItem(it)}
                                disabled={it.aiGenerating}
                                className="absolute top-1.5 right-1.5 p-1 rounded hover:bg-purple-500/20 disabled:opacity-30"
                                title="Wygeneruj caption z transkrypcji audio (Whisper + Claude)"
                              >
                                {it.aiGenerating ? (
                                  <Loader2 size={13} className="animate-spin text-purple-300" />
                                ) : (
                                  <Sparkles size={13} className="text-purple-300" />
                                )}
                              </button>
                            )}
                          </div>
                        </div>
                        <div className="flex flex-col gap-1 flex-shrink-0">
                          <button
                            onClick={() => moveItem(it.id, -1)}
                            disabled={idx === 0}
                            className="p-1 rounded hover:bg-white/[0.06] disabled:opacity-20"
                          >
                            <ArrowUp size={14} className="text-white/60" />
                          </button>
                          <button
                            onClick={() => moveItem(it.id, 1)}
                            disabled={idx === items.length - 1}
                            className="p-1 rounded hover:bg-white/[0.06] disabled:opacity-20"
                          >
                            <ArrowDown size={14} className="text-white/60" />
                          </button>
                          <button
                            onClick={() => removeItem(it.id)}
                            className="p-1 rounded hover:bg-red-500/20"
                          >
                            <Trash2 size={14} className="text-red-400" />
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </section>
        </div>

        <div className="px-6 py-4 border-t border-white/[0.08] flex items-center justify-between">
          <div className="flex items-center gap-3 min-w-0">
            <div className="text-white/50 text-xs">
              {items.length} × {selectedPlatforms.length} ={" "}
              <span className="text-white">{items.length * selectedPlatforms.length}</span> postów{" "}
              {isEditMode ? "po zapisie" : "do utworzenia"}
            </div>
            {tiktokValidity && (
              <span className="text-[11px] text-yellow-400 flex items-center gap-1">
                <AlertCircle size={11} />
                {t(`contentScheduler.presets.tiktok.gate.${tiktokValidity}`)}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
              Anuluj
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={isSubmitting || tiktokInvalid}
              className="bg-[#ef3e29] hover:bg-[#d63520] text-white"
            >
              {isSubmitting ? (
                <Loader2 size={14} className="animate-spin mr-2" />
              ) : (
                <Plus size={14} className="mr-1" />
              )}
              {isEditMode ? "Zapisz zmiany" : "Zaplanuj batch"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
